import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { AuthGuard } from './AuthGuard';
import { AuthGuardConfig } from '@/context/auth/rbacTypes';
import { useEnhancedAuthContext } from '@/context/auth/EnhancedAuthContext';

interface RouteGuardProps extends AuthGuardConfig {
  children: React.ReactNode;
  redirectTo?: string;
}

export const RouteGuard: React.FC<RouteGuardProps> = ({
  children,
  redirectTo = '/login',
  ...authConfig
}) => {
  const location = useLocation();
  const { user, isAuthenticated } = useEnhancedAuthContext();

  // Superusers bypass all checks immediately
  if (isAuthenticated && user?.is_superuser) {
    return <>{children}</>;
  }

  // For regular users, don't block on permission loading - show content optimistically
  // The AuthGuard will handle the actual permission checking
  return (
    <AuthGuard
      {...authConfig}
      redirectTo={redirectTo}
      fallback={
        isAuthenticated ? (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <h2 className="text-xl font-semibold text-foreground">Access Denied</h2>
            <p className="text-muted-foreground mt-2">You don't have permission to view this page.</p>
            <a
              href="/dashboard"
              className="mt-4 inline-flex items-center px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors"
            >
              Go to Dashboard
            </a>
          </div>
        ) : (
          <Navigate
            to={redirectTo}
            state={{ from: location }}
            replace
          />
        )
      }
    >
      {children}
    </AuthGuard>
  );
};

// Convenience route guards
export const AdminRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <RouteGuard requiredRoles={['admin']}>
    {children}
  </RouteGuard>
);

export const EmailRoute: React.FC<{ children: React.ReactNode; folder?: string }> = ({ 
  children, 
  folder 
}) => (
  <RouteGuard
    requiredPermissions={['email.read']}
    accessContext={{ resource: 'email', metadata: { folder } }}
  >
    {children}
  </RouteGuard>
);
